import { useState } from "react";
import { Compass, MessageSquareCode, LayoutTemplate, Link2, Rocket, ArrowRight } from "lucide-react";

const cards = [
  { key: "B", icon: Compass, title: "BLUEPRINT",
    tag: "Design before you build",
    lines: ["Problem", "User", "Goal", "Features", "Workflow", "App structure"],
    desc: "Define what you're building before writing a single prompt." },
  { key: "U", icon: MessageSquareCode, title: "UNDERSTAND & PROMPT",
    tag: "Turn structure into instruction",
    lines: ["Structured prompts", "Context framing", "Tool selection", "Iteration loops"],
    desc: "Translate your blueprint into instructions AI tools can execute." },
  { key: "I", icon: LayoutTemplate, title: "IMPLEMENT",
    tag: "Ship the interface",
    lines: ["Interfaces", "Pages", "Forms", "Database structures", "Core logic"],
    desc: "Build the actual product surface — pages, forms, data, features." },
  { key: "L", icon: Link2, title: "LINK, TEST & IMPROVE",
    tag: "Connect and refine",
    lines: ["Data", "Pages", "Forms", "Workflows", "Test → Debug → Improve"],
    desc: "Wire it up, break it, fix it, and improve it — the honest work." },
  { key: "D", icon: Rocket, title: "DEPLOY",
    tag: "Get to real users",
    lines: ["Production readiness", "Real users", "Launch", "Future improvements"],
    desc: "Ship it to the world and set the stage for continuous improvement." },
];

export default function BuildFramework() {
  const [active, setActive] = useState("B");
  const current = cards.find(c => c.key === active);
  const Icon = current.icon;
  return (
    <section id="framework" className="relative py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="mb-10">
          <div className="eyebrow mb-3">The Moolah Tech BUILD Framework™</div>
          <h2 className="text-3xl sm:text-5xl font-bold text-white leading-[1.05]">
            Stop randomly prompting.<br/>
            <span className="brand-gradient">Start building with a system.</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-2xl">
            A structured methodology for moving from an idea toward a functional digital product using AI-assisted development.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-3 mb-8">
          {cards.map(c => (
            <button key={c.key} data-testid={`framework-tab-${c.key.toLowerCase()}`} onClick={() => setActive(c.key)}
              className={`text-left rounded-2xl p-5 border transition-all ${
                active===c.key
                  ? "bg-gradient-to-br from-cyan-500/15 to-blue-600/10 border-cyan-400/50 shadow-[0_0_35px_rgba(56,189,248,0.15)]"
                  : "bg-white/[0.02] border-white/5 hover:border-cyan-400/30"
              }`}>
              <div className={`text-5xl font-black ${active===c.key?"text-cyan-300":"text-slate-600"}`}>{c.key}</div>
              <div className="mt-3 text-white text-sm font-semibold">{c.title}</div>
              <div className="text-[11px] text-slate-500 mt-1">{c.tag}</div>
            </button>
          ))}
        </div>

        <div className="glass p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl"/>
          <div className="grid md:grid-cols-3 gap-6 relative">
            <div className="md:col-span-1">
              <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-cyan-300"/>
              </div>
              <div className="text-cyan-300 font-mono text-xs tracking-widest uppercase">{current.key} — {current.tag}</div>
              <h3 className="text-2xl font-bold text-white mt-2">{current.title}</h3>
              <p className="text-slate-400 mt-3">{current.desc}</p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-2">
              {current.lines.map((l, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/5 rounded-lg px-4 py-3">
                  <div className="w-6 h-6 rounded-md bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 text-xs font-mono">{String(i+1).padStart(2,"0")}</div>
                  <div className="text-slate-200 text-sm">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
          {["IDEA","BUILD","LIVE APP"].map((s,i) => (
            <div key={s} className="flex items-center gap-4">
              <div className="px-5 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-200 font-mono text-sm tracking-widest">{s}</div>
              {i<2 && <ArrowRight className="w-4 h-4 text-cyan-400"/>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
